/**
 * useBeatsOnMap - the attacks as the display plays them, held by the panel (plan map-display D; design-gaps K3).
 *
 * A beat moves more than its own drawing: the charge's rush and the parry's throw-back shove the attacker, a heavy
 * or Impact landing throws the target, a heavy landing kicks the camera (engine/display/beats `shovesOf`,
 * `kickOf`). The
 * panel works the beats out ONCE here (useBeats) and hands them to the beats layer, which runs its own clock to
 * draw them; the panel's frame (useBeatFrame) runs ONLY while a beat moves something (`movesMs`), to fold the
 * shoves into the drawn tokens and the kick into the scene's style - so a plain shot never re-renders the whole
 * panel every frame (the Track K review and simplify). The figures' looks live
 * here too: a Warp Instability target backtracks when struck. The classic map plays no beat.
 *
 * @param {object} panel - the panel's props (impacts, roster, tokens)
 * @param {boolean} shown - the display is the renderer
 * @param {Record<string, object>} drawnTokens - the figures where the walks have them
 * @returns {{ beats: object[], looks: object, shoves: object, kick: {dx: number, dz: number} | null }}
 */
import { useMemo } from 'react';
import { useBeatFrame, useBeats } from '../../hooks/useDisplayBeats';
import useHoldsStill from '../../hooks/useHoldsStill';
import { kickOf, shovesOf } from '../../engine/display/beats';
import { looksFor } from './markerDecorations';
import { EMPTY } from './panelDefaults';

/** How long the panel's frame must follow a beat: while it moves something (engine/display/beats `movesMs`). */
const movesOf = (beat) => beat.movesMs;

export default function useBeatsOnMap(panel, shown, drawnTokens) {
  const still = useHoldsStill();
  const { roster, tokens } = panel;
  const looks = useMemo(() => (shown ? looksFor(roster, tokens) : EMPTY), [shown, roster, tokens]);
  const beats = useBeats(shown ? panel.impacts : undefined, drawnTokens, still, looks);
  // The panel's own frame runs only while a beat moves a figure or the camera; the beats layer draws the rest.
  const moving = useBeatFrame(beats, still, movesOf);
  return { beats, looks, shoves: shovesOf(moving), kick: kickOf(moving) };
}
